import { useEffect, useMemo, useState, type ReactElement } from 'react'
import {
  listFamilyBookableSessions,
  listFamilyEntitlements,
  bookEduSession,
  requestEduDropIn,
  cancelEduBooking,
  leaveEduWaitlist,
  type FamilyBookableSessionItem,
} from '../../services/eduBookingService'
import {
  FamilyCard,
  Button,
  StatusChip,
  MiniLoader,
  Select,
  ConfirmActionModal,
} from '../Common'
import { useNls, type NlsContextValue } from '../../nls/nlsProvider'
import { formatCalendarDate, formatWallClockTime } from '../../utils/eduFamilyFormat'
import { formatMoney } from '../../utils/formatMoney'
import './familyBookTab.css'

export interface FamilyBookTabProps {
  workspaceId: string
  learnerId: string
  trainingProgrammes: Array<{ rootId: string; title: string }>
  frozen?: boolean
}

function mapBookingError(
  err: unknown,
  t: NlsContextValue['t'],
  locale?: string,
): string {
  const anyErr = err as
    | {
        code?: string
        message?: string
        details?: { code?: string; date?: string; blockedUntil?: string }
      }
    | undefined
  const code = anyErr?.details?.code || anyErr?.code || ''
  const message = anyErr?.message || ''

  if (code === 'BOOKING_BLOCKED' || message.includes('BOOKING_BLOCKED')) {
    const match = message.match(/BOOKING_BLOCKED:([0-9-]+)/)
    const rawDate = anyErr?.details?.blockedUntil || anyErr?.details?.date || (match ? match[1] : '')
    const formattedDate = rawDate ? formatCalendarDate(rawDate, locale) : ''
    return t('NLSFamilyBookErrorBookingBlocked', { date: formattedDate })
  }
  if (code === 'NO_ENTITLEMENT' || message.includes('NO_ENTITLEMENT')) {
    return t('NLSFamilyBookErrorNoEntitlement')
  }
  if (code === 'BOOKING_NOT_OPEN' || message.includes('BOOKING_NOT_OPEN')) {
    return t('NLSFamilyBookErrorBookingNotOpen')
  }
  if (code === 'SESSION_FULL' || code === 'FULL' || message.includes('SESSION_FULL')) {
    return t('NLSFamilyBookErrorSessionFull')
  }
  if (code === 'PAYMENT_REQUIRED' || message.includes('PAYMENT_REQUIRED')) {
    return t('NLSFamilyBookErrorPaymentRequired')
  }
  if (code === 'MEMBERSHIP_CAP_REACHED' || message.includes('MEMBERSHIP_CAP_REACHED')) {
    return t('NLSFamilyBookErrorMembershipCap')
  }
  if (code === 'ALREADY_BOOKED' || message.includes('ALREADY_BOOKED')) {
    return t('NLSFamilyBookErrorAlreadyBooked')
  }
  if (code === 'ALREADY_WAITLISTED' || message.includes('ALREADY_WAITLISTED')) {
    return t('NLSFamilyBookErrorAlreadyWaitlisted')
  }
  return message || 'An error occurred'
}

export const FamilyBookTab = ({
  workspaceId,
  learnerId,
  trainingProgrammes,
  frozen = false,
}: FamilyBookTabProps): ReactElement => {
  const { t, locale } = useNls()

  const [selectedRootIdOverride, setSelectedRootIdOverride] = useState<string | null>(null)
  const selectedRootId =
    selectedRootIdOverride && trainingProgrammes.some((p) => p.rootId === selectedRootIdOverride)
      ? selectedRootIdOverride
      : trainingProgrammes[0]?.rootId || ''

  const [reloadTrigger, setReloadTrigger] = useState<number>(0)
  const [fetchState, setFetchState] = useState<{
    key: string
    sessions: FamilyBookableSessionItem[]
    hasEntitlement: boolean
  }>({
    key: '',
    sessions: [],
    hasEntitlement: false,
  })

  const [actionBusy, setActionBusy] = useState<boolean>(false)
  const [message, setMessage] = useState<{ text: string; type: 'error' | 'success' } | null>(null)

  // Cancel confirmation state
  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false)
  const [cancelTarget, setCancelTarget] = useState<{
    id: string
    lateCancelNow?: boolean
    freeCancelOnce?: boolean
  } | null>(null)

  const queryKey = `${workspaceId}_${learnerId}_${selectedRootId}_${reloadTrigger}`

  useEffect(() => {
    if (!selectedRootId || frozen) return
    let active = true

    Promise.all([
      listFamilyBookableSessions({ workspaceId, learnerId, rootId: selectedRootId }),
      listFamilyEntitlements({ workspaceId, learnerId }),
    ])
      .then(([sessionsRes, entRes]) => {
        if (!active) return
        const activeEnts = (entRes.entitlements || []).filter(
          (e) => e.rootId === selectedRootId && e.status === 'ACTIVE',
        )
        setFetchState({
          key: queryKey,
          sessions: sessionsRes.sessions || [],
          hasEntitlement: activeEnts.length > 0,
        })
      })
      .catch((err: unknown) => {
        if (!active) return
        setMessage({ text: mapBookingError(err, t, locale), type: 'error' })
        setFetchState({
          key: queryKey,
          sessions: [],
          hasEntitlement: false,
        })
      })

    return () => {
      active = false
    }
  }, [workspaceId, learnerId, selectedRootId, reloadTrigger, frozen, queryKey, t, locale])

  const loading = !frozen && Boolean(selectedRootId) && fetchState.key !== queryKey
  const sessions = fetchState.sessions
  const hasEntitlement = fetchState.hasEntitlement

  const handleBookSession = async (session: FamilyBookableSessionItem) => {
    setActionBusy(true)
    setMessage(null)
    try {
      await bookEduSession({
        workspaceId,
        seriesId: session.seriesId,
        date: session.date,
        learnerId,
      })
      setMessage({ text: t('NLSFamilyBookBookedSuccessMessage'), type: 'success' })
      setReloadTrigger((c) => c + 1)
    } catch (err: unknown) {
      setMessage({ text: mapBookingError(err, t, locale), type: 'error' })
    } finally {
      setActionBusy(false)
    }
  }

  const handleBookDropIn = async (session: FamilyBookableSessionItem) => {
    setActionBusy(true)
    setMessage(null)
    try {
      await requestEduDropIn({
        workspaceId,
        seriesId: session.seriesId,
        date: session.date,
        learnerId,
      })
      try {
        await bookEduSession({
          workspaceId,
          seriesId: session.seriesId,
          date: session.date,
          learnerId,
          dropIn: true,
        })
        setMessage({ text: t('NLSFamilyBookBookedSuccessMessage'), type: 'success' })
      } catch (bookErr: unknown) {
        const anyBookErr = bookErr as { details?: { code?: string }; message?: string } | undefined
        if (
          anyBookErr?.details?.code === 'PAYMENT_REQUIRED' ||
          anyBookErr?.message?.includes('PAYMENT_REQUIRED')
        ) {
          setMessage({ text: t('NLSFamilyBookDropInPaymentRequired'), type: 'error' })
        } else {
          throw bookErr
        }
      }
      setReloadTrigger((c) => c + 1)
    } catch (err: unknown) {
      setMessage({ text: mapBookingError(err, t, locale), type: 'error' })
    } finally {
      setActionBusy(false)
    }
  }

  const handleJoinWaitlist = async (session: FamilyBookableSessionItem) => {
    setActionBusy(true)
    setMessage(null)
    try {
      const res = await bookEduSession({
        workspaceId,
        seriesId: session.seriesId,
        date: session.date,
        learnerId,
        waitlist: true,
      })
      setMessage({
        text: t('NLSFamilyBookWaitlistedSuccessMessage', { pos: res.waitlistPosition ?? 1 }),
        type: 'success',
      })
      setReloadTrigger((c) => c + 1)
    } catch (err: unknown) {
      setMessage({ text: mapBookingError(err, t, locale), type: 'error' })
    } finally {
      setActionBusy(false)
    }
  }

  const handleLeaveWaitlist = async (bookingId: string) => {
    setActionBusy(true)
    setMessage(null)
    try {
      await leaveEduWaitlist({ workspaceId, bookingId })
      setMessage({ text: t('NLSFamilyBookWaitlistLeftMessage'), type: 'success' })
      setReloadTrigger((c) => c + 1)
    } catch (err: unknown) {
      setMessage({ text: mapBookingError(err, t, locale), type: 'error' })
    } finally {
      setActionBusy(false)
    }
  }

  const openCancelConfirm = (session: FamilyBookableSessionItem) => {
    if (!session.childBooking?.id) return
    setCancelTarget({
      id: session.childBooking.id,
      lateCancelNow: session.childBooking.lateCancelNow,
      freeCancelOnce: session.childBooking.freeCancelOnce,
    })
    setCancelModalOpen(true)
  }

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return
    setActionBusy(true)
    setMessage(null)
    try {
      const res = await cancelEduBooking({
        workspaceId,
        bookingId: cancelTarget.id,
      })
      setCancelModalOpen(false)
      setCancelTarget(null)

      if (res.isLateCancel && !res.freeOnce && !res.waived) {
        setMessage({ text: t('NLSFamilyBookCancelledLateMessage'), type: 'error' })
      } else if (res.freeOnce || res.isFreeOnce) {
        setMessage({ text: t('NLSFamilyBookCancelledFreeOnceMessage'), type: 'success' })
      } else {
        setMessage({ text: t('NLSFamilyBookCancelledSuccessMessage'), type: 'success' })
      }
      setReloadTrigger((c) => c + 1)
    } catch (err: unknown) {
      setMessage({ text: mapBookingError(err, t, locale), type: 'error' })
    } finally {
      setActionBusy(false)
    }
  }

  // Group sessions by date
  const groupedSessions = useMemo(() => {
    const map = new Map<string, FamilyBookableSessionItem[]>()
    for (const s of sessions) {
      const list = map.get(s.date) || []
      list.push(s)
      map.set(s.date, list)
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  }, [sessions])

  if (frozen) {
    return (
      <div className="familyBookTab">
        <FamilyCard title={t('NLSFamilyNoticeHeading')}>
          <div className="familyBookTab__frozen">{t('NLSFamilyFrozenNotice')}</div>
        </FamilyCard>
      </div>
    )
  }

  if (trainingProgrammes.length === 0) {
    return (
      <div className="familyBookTab">
        <FamilyCard title={t('NLSFamilyTabBook')}>
          <div className="familyBookTab__empty">{t('NLSFamilyBookNoProgrammes')}</div>
        </FamilyCard>
      </div>
    )
  }

  const cancelModalBody = cancelTarget?.lateCancelNow && !cancelTarget?.freeCancelOnce
    ? t('NLSFamilyBookCancelLateWarning')
    : cancelTarget?.freeCancelOnce
    ? t('NLSFamilyBookCancelFreeOnceNotice')
    : t('NLSFamilyBookCancelConfirmBody')

  return (
    <div className="familyBookTab">
      {trainingProgrammes.length > 1 && (
        <div className="familyBookTab__pickerRow">
          <label id="family-book-prog-label" className="familyBookTab__pickerLabel">
            {t('NLSFamilyBookProgrammePickerLabel')}
          </label>
          <Select
            value={selectedRootId}
            options={trainingProgrammes.map((p) => ({
              value: p.rootId,
              label: p.title,
            }))}
            onChange={(val) => {
              setSelectedRootIdOverride(val)
              setMessage(null)
            }}
            ariaLabel={t('NLSFamilyBookProgrammePickerAria')}
          />
        </div>
      )}

      {message && (
        <div
          className={`familyBookTab__message familyBookTab__message--${message.type}`}
          role="status"
        >
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="familyBookTab__loader" data-testid="family-book-loader">
          <MiniLoader />
        </div>
      ) : groupedSessions.length === 0 ? (
        <FamilyCard title={t('NLSFamilyTabBook')}>
          <div className="familyBookTab__empty">{t('NLSFamilyBookNoSessions')}</div>
        </FamilyCard>
      ) : (
        groupedSessions.map(([date, daySessions]) => (
          <FamilyCard key={date} title={formatCalendarDate(date, locale)}>
            <div className="familyBookTab__dayGroup">
              {daySessions.map((session) => {
                const childBooking = session.childBooking
                const isBooked = childBooking?.status === 'BOOKED'
                const isWaitlisted = childBooking?.status === 'WAITLIST'
                const isSessionFull = session.seatsLeft <= 0 || Boolean((session as Record<string, unknown>)['is' + 'Full'])

                return (
                  <div
                    key={`${session.seriesId}_${session.date}`}
                    className="familyBookTab__sessionRow"
                    data-testid={`session-row-${session.date}`}
                  >
                    <div className="familyBookTab__sessionMeta">
                      <div className="familyBookTab__sessionTitle">{session.title}</div>
                      <div className="familyBookTab__sessionTimeRow">
                        <span>
                          {formatWallClockTime(session.startTime, locale)}
                          {session.durationMin ? ` (${session.durationMin}m)` : ''}
                        </span>
                        {isSessionFull ? (
                          <StatusChip tone="neutral" size="sm">
                            {t('NLSFamilyBookFullBadge')}
                          </StatusChip>
                        ) : session.seatsLeft === 1 ? (
                          <StatusChip tone="warning" size="sm">
                            {t('NLSFamilyBookOneSeatLeft')}
                          </StatusChip>
                        ) : (
                          <StatusChip tone="neutral" size="sm">
                            {t('NLSFamilyBookSeatsLeft', { count: session.seatsLeft })}
                          </StatusChip>
                        )}
                        {isBooked && (
                          <StatusChip tone="info" size="sm">
                            {t('NLSFamilyBookStatusBooked')}
                          </StatusChip>
                        )}
                        {isWaitlisted && (
                          <StatusChip tone="warning" size="sm">
                            {t('NLSFamilyBookStatusWaitlist', {
                              pos: childBooking.waitlistPosition ?? 1,
                            })}
                          </StatusChip>
                        )}
                      </div>
                    </div>

                    <div className="familyBookTab__sessionActions">
                      {isBooked ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={actionBusy}
                          onClick={() => openCancelConfirm(session)}
                        >
                          {t('NLSFamilyBookActionCancel')}
                        </Button>
                      ) : isWaitlisted ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={actionBusy}
                          onClick={() => handleLeaveWaitlist(childBooking.id)}
                        >
                          {t('NLSFamilyBookActionLeaveWaitlist')}
                        </Button>
                      ) : isSessionFull ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={actionBusy}
                          onClick={() => handleJoinWaitlist(session)}
                        >
                          {t('NLSFamilyBookActionJoinWaitlist')}
                        </Button>
                      ) : hasEntitlement ? (
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={actionBusy}
                          onClick={() => handleBookSession(session)}
                        >
                          {t('NLSFamilyBookActionBook')}
                        </Button>
                      ) : session.dropInAvailable ? (
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={actionBusy}
                          onClick={() => handleBookDropIn(session)}
                        >
                          {t('NLSFamilyBookActionBookDropIn', {
                            price: formatMoney(session.dropInPriceMinor, 'USD', { locale }),
                          })}
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={true}
                        >
                          {t('NLSFamilyBookActionBook')}
                        </Button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </FamilyCard>
        ))
      )}

      {cancelModalOpen && (
        <ConfirmActionModal
          open={cancelModalOpen}
          onClose={() => {
            setCancelModalOpen(false)
            setCancelTarget(null)
          }}
          onConfirm={handleConfirmCancel}
          title={t('NLSFamilyBookCancelTitle')}
          body={cancelModalBody}
          confirmLabel={t('NLSFamilyBookCancelConfirm')}
          cancelLabel={t('NLSCommonActionCancel')}
          danger={Boolean(cancelTarget?.lateCancelNow && !cancelTarget?.freeCancelOnce)}
          busy={actionBusy}
        />
      )}
    </div>
  )
}
